import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star } from 'lucide-react';
import { localizePosition } from '../data/mockData';

const ProfileCard = ({ gk, onBook, onViewCard, currentUser }) => {
  const isTeam = gk.type === 'equipo';
  const isOwn = currentUser && String(currentUser.id) === String(gk.userId || gk.user_id);
  
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      whileHover={{ y: -10 }}
      className="glass profile-card"
      style={{ 
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '2rem',
        background: 'rgba(255,255,255,0.03)',
        border: isTeam ? '2px solid var(--primary)' : '1px solid rgba(255,255,255,0.1)',
        boxShadow: isTeam ? '0 0 20px rgba(212, 255, 0, 0.15)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer'
      }}
      onClick={() => onViewCard && onViewCard(gk)}
    >
    <div className="profile-image-container">
      <img src={gk.image} alt={gk.name} className="profile-image" />
      
      {/* Entity Type Badge - Colocado dentro del contenedor de imagen para alineación perfecta */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        zIndex: 10
      }}>
        <span style={{
          padding: '4px 10px',
          background: isTeam ? 'var(--primary)' : 'rgba(0,0,0,0.7)',
          color: isTeam ? '#000' : '#fff',
          borderRadius: '100px',
          fontSize: '0.65rem',
          fontWeight: '800',
          letterSpacing: '0.05em',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          textTransform: 'uppercase',
          border: isTeam ? 'none' : '1px solid rgba(255,255,255,0.2)'
        }}>
          {isTeam ? 'EQUIPO' : 'PLAYER'}
        </span>
      </div>

      {/* Disponible Hoy Badge - Alineado con la misma altura exacta a la derecha */}
      <div className="glass profile-badge" style={{ 
        top: '12px', 
        right: '12px', 
        padding: '4px 10px',
        fontSize: '0.65rem',
        fontWeight: '800',
        color: 'var(--primary)', 
        border: '1px solid rgba(var(--primary-rgb), 0.3)',
        letterSpacing: '0.02em',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
      }}>
        Disponible hoy
      </div>
    </div>
    <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.4rem', marginBottom: '0.2rem', fontWeight: 800 }}>
            {(gk.show_full_name || gk.showFullName) ? gk.name : 
              (gk.type !== 'equipo' && gk.name?.split(' ').length > 1 
                ? `${gk.name.split(' ')[0]} ${gk.name.split(' ')[1].charAt(0)}.` 
                : gk.name)}
          </h3>
          <p style={{ color: 'var(--primary)', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.4rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {localizePosition(gk.position, gk.gender)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            <MapPin size={14} color="var(--primary)" /> 
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gk.location}</span>
          </div>
        </div>
        <div className="glass" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.6rem', borderRadius: '0.75rem', border: '1px solid rgba(255,215,0,0.2)', marginLeft: '0.5rem' }}>
          <Star size={12} fill="#FFD700" color="#FFD700" />
          <span style={{ fontWeight: 800, color: '#fff', fontSize: '0.9rem' }}>{gk.rating}</span>
          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginLeft: '0.1rem' }}>VAL</span>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem', marginTop: 'auto' }}>
        {/* Category/Gender Badge */}
        {((isTeam && gk.teamCategory) || (!isTeam && gk.gender)) && (
          <span className="glass" style={{ 
            padding: '0.4rem 0.8rem', 
            borderRadius: '0.6rem', 
            fontSize: '0.8rem', 
            fontWeight: 700, 
            color: 'var(--primary)', 
            border: '1px solid rgba(212, 255, 0, 0.25)',
            whiteSpace: 'nowrap' 
          }}>
            {isTeam 
              ? gk.teamCategory 
              : (gk.gender?.toLowerCase() === 'mujer' || gk.gender?.toLowerCase() === 'femenino' ? 'Femenino' : gk.gender?.toLowerCase() === 'hombre' || gk.gender?.toLowerCase() === 'masculino' ? 'Masculino' : 'Mixto')
            }
          </span>
        )}

        {/* Mixed Availability Badge */}
        {!isTeam && gk.isMixedAvailable && (
          <span className="glass" style={{ 
            padding: '0.4rem 0.8rem', 
            borderRadius: '0.6rem', 
            fontSize: '0.8rem', 
            fontWeight: 700, 
            color: '#00E5FF', 
            border: '1px solid rgba(0, 229, 255, 0.3)',
            whiteSpace: 'nowrap' 
          }}>
            Mixto
          </span>
        )}

        {/* Court Type / Specialty Badge */}
        {gk.specialty && (
          <span className="glass" style={{ 
            padding: '0.4rem 0.8rem', 
            borderRadius: '0.6rem', 
            fontSize: '0.8rem', 
            fontWeight: 700, 
            color: '#FFB300', 
            border: '1px solid rgba(255, 179, 0, 0.3)',
            whiteSpace: 'nowrap' 
          }}>
            {gk.specialty}
          </span>
        )}

        {/* Reviews for teams */}
        {isTeam && gk.reviews !== undefined && (
          <span className="glass" style={{ padding: '0.4rem 0.8rem', borderRadius: '0.6rem', fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap' }}>
            {gk.reviews} partidos
          </span>
        )}

        {/* Attendance Rate for players */}
        {gk.type === 'jugador' && gk.matchesTotal > 0 && (
          <span className="glass" style={{ 
            padding: '0.4rem 0.8rem', 
            borderRadius: '0.6rem', 
            fontSize: '0.8rem', 
            fontWeight: 800, 
            color: gk.attendanceRate >= 90 ? '#4ade80' : gk.attendanceRate >= 70 ? '#FFD700' : '#ff4444',
            border: `1px solid ${gk.attendanceRate >= 90 ? 'rgba(74, 222, 128, 0.2)' : gk.attendanceRate >= 70 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 68, 68, 0.2)'}`,
            whiteSpace: 'nowrap' 
          }}>
            PRE {gk.matchesPlayed}/{gk.matchesTotal}
          </span>
        )}
      </div>

      {isOwn ? (
        <button 
          className="btn-outline" 
          style={{ width: '100%', justifyContent: 'center', padding: '1rem', marginTop: 'auto', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
          onClick={(e) => { e.stopPropagation(); onViewCard && onViewCard(gk); }}
        >
          {isTeam ? 'Mi Equipo' : 'Mi Perfil'}
        </button>
      ) : (
        <button 
          className="btn-primary" 
          style={{ width: '100%', justifyContent: 'center', padding: '1rem', marginTop: 'auto' }}
          onClick={(e) => { e.stopPropagation(); onBook(gk); }}
        >
          Coordinar
        </button>
      )}
    </div>
    </motion.div>
  );
};

export default ProfileCard;
